import { format } from "date-fns";
import type { Timestamp } from "firebase/firestore";
import { wallClockParts, type TimeFormatPreference } from "@lemonadetraining/contracts";
import { toEpochMs } from "./firestoreTime";

/** What the preference resolves to. "system" never escapes `resolveTimeFormat`. */
export type ResolvedTimeFormat = "24h" | "12h";

/** A Timestamp that crossed a callable boundary and arrived as plain JSON. */
type SerializedTimestamp = {
  seconds?: number;
  nanoseconds?: number;
  _seconds?: number;
  _nanoseconds?: number;
};

/**
 * Every shape a time of day reaches the dashboard in. Strings are either a
 * wall-clock `"HH:mm"` (the schedule-template slot contract) or an ISO-8601
 * date *and* time from a rehydrated store; anything else is not a time of day
 * and renders as absent rather than as midnight.
 */
export type TimeValue = Date | Timestamp | SerializedTimestamp | string | number | null | undefined;

export type TimeFormatContext = {
  /** `null` when the browser locale could not be probed. */
  deviceUses24Hour: boolean | null;
};

export const TIME_PLACEHOLDER = "--:--";

const WALL_CLOCK = /^([01]?\d|2[0-3]):([0-5]\d)$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/**
 * Below this a value is an index or an hour, above it `new Date` is invalid.
 * Local midnight on the reference day sits below it east of UTC, so a time with
 * no date must be passed as a wall-clock string, never as a Date.
 */
const MIN_EPOCH_MS = Date.UTC(2000, 0, 1);
const MAX_EPOCH_MS = 8.64e15;

// `hourCycle` is absent from this TypeScript version's ES2020 Intl lib.
type ResolvedHourOptions = Intl.ResolvedDateTimeFormatOptions & {
  hourCycle?: "h11" | "h12" | "h23" | "h24";
};

/**
 * Reads what the browser locale resolved to; it never sets `hour12`, which
 * selects h24 on this runtime and would render midnight as hour 24. Engines
 * that report no hour cycle fall back to `hour12`.
 */
export function probeDeviceUses24Hour(): boolean | null {
  try {
    const options = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
    }).resolvedOptions() as ResolvedHourOptions;
    if (options.hourCycle) return options.hourCycle === "h23" || options.hourCycle === "h24";
    if (typeof options.hour12 === "boolean") return !options.hour12;
    return null;
  } catch {
    return null;
  }
}

let cachedDeviceUses24Hour: boolean | null | undefined;

/**
 * Probed at most once per page load; the browser locale cannot change within
 * one. `null` is a real result, so the sentinel is `undefined`.
 */
export function getDeviceUses24Hour(): boolean | null {
  if (cachedDeviceUses24Hour === undefined) cachedDeviceUses24Hour = probeDeviceUses24Hour();
  return cachedDeviceUses24Hour;
}

/**
 * Anything that is not an explicit choice follows the device, including an
 * unrecognised value from a client newer than this build.
 */
export function resolveTimeFormat(
  preference: TimeFormatPreference | null | undefined,
  ctx: TimeFormatContext
): ResolvedTimeFormat {
  if (preference === "24h") return "24h";
  if (preference === "12h") return "12h";
  return ctx.deviceUses24Hour === true ? "24h" : "12h";
}

const render = (date: Date, resolved: ResolvedTimeFormat) =>
  format(date, resolved === "24h" ? "HH:mm" : "h:mm a");

let warnedMissingZone = false;

/**
 * Wall-clock strings render directly; everything else normalizes through
 * `toEpochMs` rather than `toDate`, which answers "now" for garbage objects and
 * for epoch numbers. Anything left that cannot be a time of day renders as
 * absent rather than as a plausible wrong one. An instant reads on `timeZone`'s
 * clock; `null` means this browser's clock, and an unusable zone renders absent.
 */
export function formatTimeOfDay(
  value: TimeValue,
  resolved: ResolvedTimeFormat,
  timeZone: string | null
): string {
  if (timeZone === undefined) {
    if (process.env.NODE_ENV !== "production") {
      throw new Error("formatTimeOfDay needs a time zone, or null for the browser's clock");
    }
    if (!warnedMissingZone) {
      warnedMissingZone = true;
      console.error("formatTimeOfDay called without a time zone");
    }
    return TIME_PLACEHOLDER;
  }
  if (value === null || value === undefined || value === "") return TIME_PLACEHOLDER;

  if (typeof value === "string") {
    const wallClock = WALL_CLOCK.exec(value);
    if (wallClock) {
      const onAnyDay = new Date(2000, 0, 1, Number(wallClock[1]), Number(wallClock[2]));
      return render(onAnyDay, resolved);
    }
    if (!ISO_DATE.test(value)) return TIME_PLACEHOLDER;
  }

  const ms = toEpochMs(value);
  if (!Number.isFinite(ms) || ms < MIN_EPOCH_MS || ms > MAX_EPOCH_MS) {
    return TIME_PLACEHOLDER;
  }
  if (timeZone === null) return render(new Date(ms), resolved);
  const parts = wallClockParts(ms, timeZone);
  return parts
    ? render(new Date(2000, 0, 1, parts.hour, parts.minute), resolved)
    : TIME_PLACEHOLDER;
}

export const _resetMissingZoneWarningForTests = (): void => {
  warnedMissingZone = false;
};
